/**
 * EvidenceCard — one intake card: header + status, its form (from the registry
 * inputs schema), a Run button, the result, and Maltego-style SUGGESTED-NEXT-STEP
 * chips (spec §5) after it produces a node — one click runs the next operator.
 */
import { Play, Loader2, CheckCircle2, AlertTriangle, X, FileSearch, Sparkles } from 'lucide-react';
import { evidenceById, operatorsForType, STATUS_META, type InputField } from '../../../case-canvas/registry';
import { useIntakeStore, type IntakeCard } from '../../../stores/useIntakeStore';
import { runCard, retryCardSave } from '../../../case-canvas/intake/cardRunner';
import { runOperator } from '../../../case-canvas/jobs';
import { CardForm } from './CardForm';
import { useCaseWriteBlock } from '../../../case-canvas/caseAccess';
import {
  SAMPLE_NCRP_JSON,
  SAMPLE_DEVICE_JSON,
  SAMPLE_BANK_CSV,
  SAMPLE_WALLET,
} from '../../../data/fusionIntakeSamples';

const FALLBACK_INPUTS: Record<string, InputField[]> = {
  file: [{ key: 'file', kind: 'file', label: 'File', accept: '*/*', required: true }],
  paste: [{ key: 'value', kind: 'text', label: 'Value', required: true }],
  json: [{ key: 'value', kind: 'textarea', label: 'JSON' }],
};

function applySample(cardId: string, registryId: string) {
  const setInput = useIntakeStore.getState().setInput;
  if (registryId === 'E8') {
    setInput(cardId, 'value', SAMPLE_NCRP_JSON);
    return;
  }
  if (registryId === 'E4') {
    setInput(cardId, 'value', SAMPLE_WALLET);
    return;
  }
  if (registryId === 'E3') {
    setInput(cardId, 'value', SAMPLE_DEVICE_JSON);
    return;
  }
  if (registryId === 'E1') {
    const file = new File([SAMPLE_BANK_CSV], 'frozen-account-demo.csv', { type: 'text/csv' });
    setInput(cardId, 'file', file);
    setInput(cardId, 'holder', 'Ramesh Kumar');
  }
}

export function EvidenceCard({ card }: { card: IntakeCard }) {
  const setInput = useIntakeStore((s) => s.setInput);
  const removeCard = useIntakeStore((s) => s.removeCard);
  const startedFrom = useIntakeStore((s) => s.startedFrom);
  const { blocked, reason: blockedReason } = useCaseWriteBlock();
  const ev = evidenceById(card.registryId);
  if (!ev) return null;
  const fields = ev.inputs ?? FALLBACK_INPUTS[ev.input] ?? FALLBACK_INPUTS.paste;
  const m = STATUS_META[ev.status];
  const running = card.status === 'running';
  const chips = card.producedType ? operatorsForType(card.producedType).slice(0, 4) : [];
  const showSample = ['E1', 'E3', 'E4', 'E8'].includes(card.registryId)
    && card.status === 'idle'
    && !card.inputs.value
    && !(card.inputs.file instanceof File);

  return (
    <div className="w-72 shrink-0 rounded-lg border border-border bg-card shadow-sm" data-testid={`intake-card-${card.registryId}`}>
      <div className="flex items-center gap-2 px-3 py-2 border-b border-border">
        <ev.icon size={15} className="text-muted-foreground shrink-0" />
        <span className="text-xs font-semibold flex-1 truncate">{ev.name}</span>
        <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: m.color }} title={m.label} />
        <button type="button" onClick={() => removeCard(card.id)} className="text-muted-foreground hover:text-foreground" aria-label="Remove card"><X size={13} /></button>
      </div>

      <div className="p-3 space-y-2">
        {showSample && (
          <button
            type="button"
            onClick={() => applySample(card.id, card.registryId)}
            className="w-full flex items-center justify-center gap-1.5 px-2 py-1 rounded border border-dashed border-primary/40 text-[10px] text-primary hover:bg-primary/10"
            data-testid={`intake-load-sample-${card.registryId}`}
          >
            <Sparkles size={11} /> Load sample for demo
            {startedFrom ? ` (${startedFrom})` : ''}
          </button>
        )}

        <CardForm fields={fields} values={card.inputs} onChange={(k, v) => setInput(card.id, k, v)} disabled={running} />

        <button type="button" onClick={() => void runCard(card)} disabled={running || blocked}
          title={blocked ? blockedReason : undefined}
          data-testid={`intake-run-${card.registryId}`}
          className="w-full flex items-center justify-center gap-1.5 px-3 py-1.5 rounded bg-primary text-primary-foreground text-xs font-medium hover:bg-brand-gold-hover disabled:opacity-50">
          {running ? <Loader2 size={13} className="animate-spin" /> : <Play size={13} />} {running ? 'Running…' : 'Run'}
        </button>

        {card.status === 'done' && card.message && (
          <div className="flex items-start gap-1.5 text-[10px] text-emerald-600"><CheckCircle2 size={12} className="mt-0.5 shrink-0" /><span>{card.message}</span></div>
        )}
        {card.status === 'error' && card.message && (
          <div className="flex items-start gap-1.5 text-[10px] text-red-500"><AlertTriangle size={12} className="mt-0.5 shrink-0" /><span>{card.message}</span></div>
        )}
        {card.saveFailed && card.status !== 'running' && (
          <button type="button" onClick={() => void retryCardSave(card)} disabled={blocked}
            data-testid={`intake-retry-save-${card.registryId}`}
            className="w-full px-3 py-1 rounded border border-red-500/50 text-[11px] text-red-500 hover:bg-red-500/10 disabled:opacity-50">
            Retry save to the case ({card.saveFailed.items.length})
          </button>
        )}

        {chips.length > 0 && card.producedNodeId && (
          <div className="pt-1 border-t border-border">
            <div className="flex items-center gap-1 text-[9px] uppercase tracking-wide text-muted-foreground mb-1"><FileSearch size={10} /> Suggested next step</div>
            <div className="flex flex-wrap gap-1">
              {chips.map((op) => (
                <button key={op.id} type="button" onClick={() => runOperator(op, { nodeId: card.producedNodeId!, nodeType: card.producedType || '' })}
                  disabled={blocked} title={blocked ? blockedReason : undefined}
                  data-testid={`intake-next-${op.id}`}
                  className="disabled:opacity-50 disabled:cursor-not-allowed px-1.5 py-0.5 rounded-full border border-primary/40 bg-primary/10 text-[10px] text-primary hover:bg-primary/20">
                  {op.name.replace(/^.*▸ /, '')}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
